import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  DollarSign,
  Camera,
  Store,
  Loader2,
  Building2,
  Pencil,
  CheckSquare,
  Star,
  Tag,
  FileText,
  ClipboardList,
  MessageSquareText,
} from 'lucide-react';
import { PhotoTagEntityType } from '@peles/shared';
import { useAuth } from '../context/AuthContext';
import { useEvent } from '../hooks/useEvents';
import { useExpenseItems } from '../hooks/useExpenses';
import { useEventClientLedger } from '../hooks/useClientPayments';
import { ExpenseListView } from '../components/expenses/ExpenseListView';
import { EventVendorListView } from '../components/vendors/EventVendorListView';
import { EventUserListView } from '../components/common/EventUserListView';
import { PhotoListView } from '../components/photos/PhotoListView';
import { EntityModal, PageSectionsLayout } from '../components/common';
import type { SectionDef } from '../components/common';
import {
  EventDetailPanel,
  EventLogo,
  EventTagsModal,
  EventSummaryPrintModal,
} from '../components/events';
import { ClientLedgerView } from '../components/client-payments';
import { ItemizedListModal } from '../components/planning/ItemizedListModal';
import { FileExplorer } from '../components/files/FileExplorer';
import { useEnsureEntityFolder } from '../hooks/useDocumentFolders';

export function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { data: event, isLoading } = useEvent(eventId || '');
  const { data: expenseItems = [] } = useExpenseItems(eventId ? { eventId } : undefined);
  const { data: ledger } = useEventClientLedger(eventId);
  const [showEdit, setShowEdit] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [showPrintReport, setShowPrintReport] = useState(false);
  const [showItemizedReport, setShowItemizedReport] = useState(false);
  const [showItemizedList, setShowItemizedList] = useState(false);
  const [eventFolderId, setEventFolderId] = useState<string | null>(null);
  const ensureEntityFolder = useEnsureEntityFolder();

  useEffect(() => {
    if (event?.id && event?.name && !eventFolderId) {
      ensureEntityFolder
        .mutateAsync({ entityType: 'EVENT', entityId: event.id, entityName: event.name })
        .then((folder) => setEventFolderId(folder.id))
        .catch(() => {
          /* best effort */
        });
    }
  }, [event?.id, event?.name]); // eslint-disable-line

  // Total Cost = sum of item.price across event expenses
  const totalCost = expenseItems.reduce((sum, item) => sum + item.price, 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-600" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">Event not found</p>
        <button
          onClick={() => navigate('/events')}
          className="mt-4 text-primary-600 hover:text-primary-700 text-sm"
        >
          Back to Events
        </button>
      </div>
    );
  }

  const eventDate = event.startAt
    ? new Date(event.startAt).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const sections: SectionDef[] = [
    {
      id: 'clients',
      title: 'Clients',
      content: <EventUserListView eventId={eventId!} />,
    },
    {
      id: 'vendors',
      title: 'Vendors',
      content: (
        <EventVendorListView
          eventId={eventId!}
          eventName={event.name}
          eventDate={eventDate ?? undefined}
          venueId={(event as any).venueId}
          currentUser={
            currentUser ? { email: currentUser.email, name: currentUser.name } : undefined
          }
        />
      ),
    },
    {
      id: 'expenses',
      title: 'Expenses',
      content: (
        <ExpenseListView
          entityType="EVENT"
          entityId={eventId}
          currentUser={currentUser ?? undefined}
        />
      ),
    },
    {
      id: 'client-ledger',
      title: 'Client Ledger',
      content: <ClientLedgerView eventId={eventId!} />,
    },
    {
      id: 'photos',
      title: 'Photos',
      content: (
        <PhotoListView
          entityType={PhotoTagEntityType.EVENT}
          entityId={eventId}
          limit={12}
          enableUpload
          currentUser={currentUser || undefined}
        />
      ),
    },
    {
      id: 'documents',
      title: 'Documents',
      content: (
        <div className="h-96">
          <FileExplorer rootFolderId={eventFolderId ?? undefined} embedded />
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-4">
            <EventLogo
              eventId={event.id}
              eventLogoUrl={event.logoUrl}
              editable
              sizeClassName="w-14 h-14"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{event.name}</h1>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
                {eventDate && (
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {eventDate}
                  </span>
                )}
                {(event as any).eventTypeName && (
                  <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                    {(event as any).eventTypeName}
                  </span>
                )}
                {(event as any).venueName && (
                  <button
                    onClick={() =>
                      (event as any).venueId && navigate(`/venues/${(event as any).venueId}`)
                    }
                    className="flex items-center gap-1 hover:text-primary-600 transition-colors"
                  >
                    <MapPin size={14} />
                    {(event as any).venueName}
                  </button>
                )}
                {event.status && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium capitalize">
                    {event.status.toLowerCase().replace('_', ' ')}
                  </span>
                )}
                {ledger && ledger.totalOwed > 0 && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      ledger.balance > 0.005
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                    title="Amount the client still owes the producer"
                  >
                    Balance Due: {formatCurrency(Math.max(0, ledger.balance))}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowEdit(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Pencil size={16} />
              Edit
            </button>
            <button
              onClick={() => setShowTags(true)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="tags-button"
            >
              <Tag size={16} />
              Tags
            </button>
            <button
              onClick={() => navigate(`/events/${eventId}/guests`)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Users size={16} />
              Guests
            </button>
            <button
              onClick={() => navigate(`/events/${eventId}/timeline`)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Calendar size={16} />
              Timeline
            </button>
            <button
              onClick={() => navigate(`/events/${eventId}/checklist`)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              <CheckSquare size={16} />
              Task Lists
            </button>
            <button
              onClick={() => navigate(`/events/${eventId}/kibuddim`)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Star size={16} />
              Kibuddim
            </button>
            <button
              onClick={() => navigate(`/events/${eventId}/floor-plan`)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Building2 size={16} />
              Floor Plan
            </button>
            <button
              onClick={() => setShowItemizedList(true)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="itemized-list-button"
            >
              <ClipboardList size={16} />
              Itemized List
            </button>
            <button
              onClick={() => navigate(`/events/${eventId}/questionnaires`)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="questionnaires-button"
            >
              <MessageSquareText size={16} />
              Questionnaires
            </button>
            <button
              onClick={() => setShowPrintReport(true)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="print-report-button"
            >
              <FileText size={16} />
              Summary PDF
            </button>
            <button
              onClick={() => setShowItemizedReport(true)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="itemized-report-button"
            >
              <FileText size={16} />
              Itemized PDF
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <QuickStat
          icon={Users}
          label="Guests"
          value={(event as any).guestCount?.toString() || '0'}
          color="bg-blue-500"
        />
        <QuickStat
          icon={DollarSign}
          label="Total Cost"
          value={formatCurrency(totalCost)}
          color="bg-green-500"
        />
        <QuickStat icon={Camera} label="Photos" value="—" color="bg-purple-500" />
        <QuickStat icon={Store} label="Vendors" value="—" color="bg-amber-500" />
      </div>

      {/* Sections */}
      <PageSectionsLayout
        pageKey="event-detail"
        userId={currentUser?.id ?? 'guest'}
        sections={sections}
      />

      {/* Edit Event Modal */}
      {currentUser && (
        <EntityModal
          open={showEdit}
          onClose={() => setShowEdit(false)}
          title={`Edit: ${event.name}`}
          size="lg"
        >
          <EventDetailPanel
            eventId={eventId!}
            onClose={() => setShowEdit(false)}
            onUnsavedChange={() => {}}
            currentUser={currentUser}
          />
        </EntityModal>
      )}

      {/* Event Tags Modal */}
      {showTags && eventId && (
        <EventTagsModal eventId={eventId} onClose={() => setShowTags(false)} />
      )}

      {/* Event Summary Print Modal (by vendor) */}
      {showPrintReport && (
        <EventSummaryPrintModal
          event={event as any}
          expenses={expenseItems}
          ledger={ledger}
          onClose={() => setShowPrintReport(false)}
        />
      )}

      {/* Itemized (inventory) list — view, filter, add and edit items */}
      <ItemizedListModal
        open={showItemizedList}
        onClose={() => setShowItemizedList(false)}
        eventId={eventId!}
      />

      {/* Event Itemized Print Modal (by item) */}
      {showItemizedReport && (
        <EventSummaryPrintModal
          event={event as any}
          expenses={expenseItems}
          ledger={ledger}
          groupBy="item"
          onClose={() => setShowItemizedReport(false)}
        />
      )}
    </div>
  );
}

function QuickStat({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: typeof Calendar;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(amount);
}
